import { ChevronsUpDown, MoreVertical, Plus, Trash2 } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { UseFormReturn } from "react-hook-form"
import { toast } from "sonner"
import { AutocompleteInput } from "@/components/forms/autoCompleteInput"
import { CustomInput } from "@/components/forms/customInput"
import { CustomTextarea } from "@/components/forms/customTextarea"
import { MultiSelectDropdown } from "@/components/forms/multiSelectDropdown"
import { TagsInput } from "@/components/forms/tagsInput"
import {
  type LocalMediaItem,
  type MediaChanges,
  MediaUploadSection,
} from "@/components/media/mediaUploadSection"
import { SelectCommunityUsersModal } from "@/components/modals/selectCommunityUsersModal"
import { BackButton } from "@/components/shared/BackButton"
import type { AccordionItem, AccordionRef } from "@/components/ui/accordion"
import { Accordion } from "@/components/ui/accordion"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { COMMUNITY_MEDIA_LIMITS } from "@/constants/media"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useNativeCommunityVideos } from "@/features/communities/hooks/useNativeCommunityVideos"
import type { CommunityMember } from "@/features/communities/services/community.service"
import { useFormSidebarPositioning } from "@/hooks/useFormSidebarPositioning"
import { useTranslation } from "@/language"
import { cn } from "@/lib"
import type { CreateCommunityFormData } from "../schemas/community.schema"
import type { CommunityUserWithRole, LocationData } from "../types"
import type { SectionValidationResult } from "../utils/accordionValidation"
import {
  validateBasicSection,
  validateGuidelinesSection,
  validateMediaSection,
  validatePrivacySection,
  validateTagsSection,
  validateUsersSection,
} from "../utils/accordionValidation"
import { getCommunitySectionTips } from "../utils/sectionTips"

const toCapitalCase = (str: string) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()

export interface MediaFilesCreate {
  images: Array<{ id: string; file?: File; src?: string; isCover: boolean; uploadId?: string }>
  videos: Array<{
    id: string
    file?: File
    url?: string
    title?: string
    posterFile?: File
    posterUrl?: string
  }>
  orderedMediaIds?: string[]
  aiGenerationId?: string | null
  aiGenerationImageIndex?: number | null
}

export interface CommunityFormProps {
  mode: "create" | "edit"
  form: UseFormReturn<CreateCommunityFormData>
  communityId?: string
  tags: string[]
  onAddTag: (tag: string) => void
  onRemoveTag: (tag: string) => void
  selectedUsers: CommunityUserWithRole[]
  onAddUsers: (users: CommunityUserWithRole[]) => void
  onRemoveUser: (userId: string) => void
  onUpdateUserRoles: (userId: string, newRoleIds: string[]) => void
  communityType: string
  onCommunityTypeChange: (type: "public" | "private") => void
  privacySettings: {
    addToRituals: string[]
    comment: string[]
  }
  onAddToRitualsChange: (selected: string[]) => void
  onCommentChange: (selected: string[]) => void
  selectedLocation: LocationData | null
  onLocationSelect: (location: LocationData | null) => void
  communityRoles: Array<{ id: string; title: string }>
  ownerRoleId?: string
  isSubmitting: boolean
  submitError: string | null
  onSubmit: () => Promise<void>
  createMediaFiles?: MediaFilesCreate
  onMediaFilesChange?: (files: MediaFilesCreate) => void
  /** Create flow only: receives the native-video flushPending so the create
   *  page can upload videos queued before the community existed. */
  registerNativeVideoFlush?: (flush: (newCommunityId: string) => Promise<void>) => void
  /** Reports whether a native video upload is currently in flight (or queued),
   *  so the hosting page can guard against navigating away mid-upload. */
  onUploadingChange?: (uploading: boolean) => void
  renderModals?: () => React.ReactNode
  editProps?: {
    community?: {
      media?: Array<{
        communityMediaId?: string
        id?: string
        uploadId?: string
        filePath?: string
        mediaType?: string
        isCover?: boolean
        sortOrder?: number
        videoTitle?: string | null
        thumbnailFilePath?: string | null
        thumbnailUploadId?: string | null
      }>
    }
    mediaChanges: MediaChanges | null
    onMediaChange: (changes: MediaChanges) => void
    hasCoverImage: boolean
    onCoverImageChange: (hasCover: boolean) => void
    removedUserIds: Set<string>
    allMembers: CommunityMember[]
    membersLoading: boolean
    hasNextPage: boolean
    isFetchingNextPage: boolean
    loadMoreRef: React.RefObject<HTMLDivElement | null> | ((node: HTMLDivElement | null) => void)
  }
}

export function CommunityForm({
  mode,
  form,
  communityId,
  tags,
  onAddTag,
  onRemoveTag,
  selectedUsers,
  onAddUsers,
  onRemoveUser,
  onUpdateUserRoles,
  communityType,
  onCommunityTypeChange,
  privacySettings,
  onAddToRitualsChange,
  onCommentChange,
  selectedLocation,
  onLocationSelect,
  communityRoles,
  ownerRoleId,
  isSubmitting,
  submitError,
  onSubmit,
  createMediaFiles,
  onMediaFilesChange,
  registerNativeVideoFlush,
  onUploadingChange,
  renderModals,
  editProps,
}: CommunityFormProps) {
  const { t } = useTranslation("communities")
  const currentUser = useAuthStore(state => state.user)

  // Native (Cloudflare) community videos, rendered inline in the shared
  // MediaUploadSection grid (parity with rituals). communityId is empty during
  // create -> the hook queues uploads for flushPending after the community exists.
  const {
    videos: nativeVideos,
    isVideoFile,
    uploadVideo: uploadNativeVideo,
    deleteVideo: deleteNativeVideo,
    flushPending: flushPendingVideos,
    hasActiveUpload,
  } = useNativeCommunityVideos(communityId ?? "")

  useEffect(() => {
    registerNativeVideoFlush?.(flushPendingVideos)
  }, [registerNativeVideoFlush, flushPendingVideos])

  // Surface in-flight native uploads so the page can warn before the user
  // navigates away and loses them. The hook owns the status taxonomy.
  useEffect(() => {
    onUploadingChange?.(hasActiveUpload)
  }, [hasActiveUpload, onUploadingChange])

  const {
    register,
    formState: { errors, isValid },
    trigger,
    clearErrors,
    setValue,
    watch,
  } = form

  const roleOptions = communityRoles.map(role => ({
    value: role.id,
    label: toCapitalCase(role.title),
  }))

  const nameValue = watch("name")
  const handleValue = watch("handle")
  const descriptionValue = watch("description")
  const guidelinesValue = watch("guidelines")
  const typeValue = watch("type")

  const [mediaError, setMediaError] = useState<string | null>(null)
  const [isUserModalOpen, setIsUserModalOpen] = useState(false)

  const editInitialMedia = useMemo<LocalMediaItem[] | undefined>(() => {
    if (mode !== "edit" || !editProps?.community?.media) return undefined
    return editProps.community.media.map(m => ({
      id: m.communityMediaId || m.id || "",
      uploadId: m.uploadId,
      src: m.filePath || "",
      mediaType: (m.mediaType === "IMAGE" ? "IMAGE" : "VIDEO") as "IMAGE" | "VIDEO" | "AUDIO",
      isCover: m.isCover || false,
      sortOrder: m.sortOrder || 0,
      title: m.videoTitle ?? null,
      videoTitle: m.videoTitle ?? null,
      thumbnailSrc: m.thumbnailFilePath,
      thumbnailUploadId: m.thumbnailUploadId,
    }))
  }, [mode, editProps?.community?.media])
  const [attemptedSections, setAttemptedSections] = useState<Set<string>>(
    new Set(
      mode === "edit"
        ? ["community-info", "description", "upload-media", "users-roles", "guidelines", "privacy"]
        : ["community-info"],
    ),
  )
  const [openAccordionId, setOpenAccordionId] = useState<string | null>("community-info")
  const [tipModalOpen, setTipModalOpen] = useState(false)
  const [tipModalSectionId, setTipModalSectionId] = useState<string | null>(null)

  const { sidebarOffset, accordionContainerRef, setAccordionRef } =
    useFormSidebarPositioning(openAccordionId)

  const accordionRef = useRef<AccordionRef>(null)

  const sectionValidation = useMemo(() => {
    const formValues = {
      name: nameValue,
      handle: handleValue,
      description: descriptionValue,
      guidelines: guidelinesValue,
      privacySettings,
    } as CreateCommunityFormData

    const baseValidation: Record<string, SectionValidationResult> = {
      "community-info": validateBasicSection(formValues, selectedLocation),
      description: validateTagsSection(formValues, selectedLocation),
      "users-roles": validateUsersSection(formValues, selectedUsers),
      guidelines: validateGuidelinesSection(formValues),
      privacy: validatePrivacySection(formValues),
    }

    if (mode === "edit" && editProps) {
      const items = editProps.mediaChanges?.mediaItems ?? []
      const imgs = items.filter(i => i.mediaType === "IMAGE").length
      const vids = items.filter(i => i.mediaType === "VIDEO").length
      const parts: string[] = []
      if (imgs > 0) parts.push(`${imgs} image${imgs !== 1 ? "s" : ""}`)
      if (vids > 0) parts.push(`${vids} video${vids !== 1 ? "s" : ""}`)
      baseValidation["upload-media"] = {
        isComplete:
          (editProps.mediaChanges?.mediaItems?.length ?? 0) > 0 && editProps.hasCoverImage,
        summary: parts.length > 0 ? parts.join(", ") : "No media added",
      }
    } else {
      baseValidation["upload-media"] = validateMediaSection(
        createMediaFiles ?? { images: [], videos: [] },
      )
    }

    return baseValidation
  }, [
    mode,
    nameValue,
    handleValue,
    descriptionValue,
    guidelinesValue,
    privacySettings,
    selectedLocation,
    selectedUsers,
    createMediaFiles,
    editProps,
  ])

  const handleAccordionOpenChange = useCallback((openItems: Set<string>) => {
    const openItem = Array.from(openItems)[0]
    setOpenAccordionId(openItem || null)

    if (openItems.size > 0) {
      setAttemptedSections(prev => new Set([...prev, ...openItems]))
    }
  }, [])

  const shouldShowError = useCallback(
    (sectionId: string, fieldName: string) => {
      if (mode === "edit")
        return (errors as Record<string, { message?: string }>)[fieldName]?.message
      return attemptedSections.has(sectionId)
        ? (errors as Record<string, { message?: string }>)[fieldName]?.message
        : undefined
    },
    [mode, errors, attemptedSections],
  )

  const handleTipClick = useCallback((sectionId: string) => {
    setTipModalSectionId(sectionId)
    setTipModalOpen(true)
  }, [])

  // Memoized callback for media changes to prevent unnecessary re-renders
  const handleMediaChange = useCallback(
    (changes: MediaChanges) => {
      onMediaFilesChange?.({
        images: changes.mediaItems
          .filter(i => i.mediaType === "IMAGE")
          .map(i => ({
            id: i.id,
            src: i.src,
            file: i.file,
            isCover: i.isCover,
            uploadId: i.uploadId,
          })),
        videos: changes.mediaItems
          .filter(i => i.mediaType === "VIDEO")
          .map(i => ({
            id: i.id,
            file: i.file,
            url: i.src,
            title: i.title ?? undefined,
            posterFile: i.thumbnailFile,
          })),
        orderedMediaIds: changes.orderedMediaIds,
      })
      if (changes.mediaItems.some(i => i.mediaType === "IMAGE")) {
        setMediaError(null)
      }
    },
    [onMediaFilesChange],
  )

  const getAccordionItems = useCallback((): AccordionItem[] => {
    const tKey = mode === "create" ? "createCommunity" : "editCommunity"

    const items: AccordionItem[] = [
      {
        id: "community-info",
        title: t(`${tKey}.communityInfo`),
        showNextButton: true,
        isComplete: sectionValidation["community-info"]?.isComplete,
        hasError:
          mode === "edit"
            ? !sectionValidation["community-info"]?.isComplete
            : !sectionValidation["community-info"]?.isComplete &&
              attemptedSections.has("community-info"),
        summary: sectionValidation["community-info"]?.summary,
        onInfoClick: () => handleTipClick("community-info"),
        content: (
          <div className="space-y-4 mb-5">
            <p className="text-sm mb-7 mt-2 ms-1 text-body">{t(`${tKey}.basicInfo`)}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
              <CustomInput
                {...register("name")}
                id="community-name"
                label={t(`${tKey}.name`)}
                required
                placeholder={t(`${tKey}.namePlaceholder`) || t(`${tKey}.placeholders.name`)}
                error={shouldShowError("community-info", "name")}
                onClearError={() => clearErrors("name")}
              />

              <CustomInput
                {...register("handle")}
                id="community-handle"
                label={t(`${tKey}.handle`)}
                required
                prefix={mode === "create" ? "@" : undefined}
                placeholder={t(`${tKey}.handlePlaceholder`) || t(`${tKey}.placeholders.handle`)}
                error={shouldShowError("community-info", "handle")}
                onClearError={() => clearErrors("handle")}
              />
            </div>

            <div className="flex items-center gap-3 ms-1">
              <Checkbox
                id="is-organization"
                checked={watch("isOrganization")}
                onCheckedChange={checked => setValue("isOrganization", checked === true)}
                className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
              />
              <label
                htmlFor="is-organization"
                className="text-sm text-body cursor-pointer select-none"
              >
                This community represents a company or organization
              </label>
            </div>
          </div>
        ),
      },
      {
        id: "description",
        title:
          mode === "create"
            ? t("createCommunity.description")
            : t("editCommunity.descriptionSocialLinks"),
        showNextButton: true,
        isComplete: sectionValidation.description?.isComplete,
        hasError:
          mode === "edit"
            ? !sectionValidation.description?.isComplete
            : !sectionValidation.description?.isComplete && attemptedSections.has("description"),
        summary: sectionValidation.description?.summary,
        onInfoClick: () => handleTipClick("description"),
        content: (
          <div className="space-y-6 mb-5">
            <div>
              <p className="text-[14px] font-monteserat mb-7 mt-2 text-body">
                {mode === "create"
                  ? t("createCommunity.descriptionOverview")
                  : t("editCommunity.descriptionOverview")}
              </p>
              <CustomTextarea
                {...register("description")}
                id="community-description"
                label={t(`${mode === "create" ? "createCommunity" : "editCommunity"}.description`)}
                required
                className={mode === "create" ? "leading-[25px]" : undefined}
                placeholder={
                  mode === "create"
                    ? t("createCommunity.descriptionPlaceholder")
                    : t("editCommunity.placeholders.description")
                }
                error={shouldShowError("description", "description")}
                onClearError={() => clearErrors("description")}
                rows={4}
              />
            </div>

            <div>
              <TagsInput
                id="community-tags"
                label={t(`${mode === "create" ? "createCommunity" : "editCommunity"}.tags`)}
                tags={tags}
                placeholder={
                  mode === "create"
                    ? t("createCommunity.placeholders.tags")
                    : t("editCommunity.placeholders.tags")
                }
                required={true}
                onAddTag={onAddTag}
                onRemoveTag={onRemoveTag}
                error={shouldShowError("description", "tags")}
                onClearError={() => clearErrors("tags")}
                enableSearch={true}
              />
            </div>

            <div>
              <AutocompleteInput
                label={t(`${mode === "create" ? "createCommunity" : "editCommunity"}.address`)}
                value={
                  watch("address") ||
                  (mode === "edit" ? selectedLocation?.address : undefined) ||
                  ""
                }
                mode="full"
                onLocationSelect={locationData => {
                  setValue("address", locationData.address)
                  onLocationSelect(locationData)
                }}
                onInputChange={value => {
                  setValue("address", value)
                }}
                placeholder={
                  mode === "create"
                    ? t("createCommunity.addressPlaceholder")
                    : t("editCommunity.placeholders.location")
                }
                error={shouldShowError("description", "address")}
                onClearError={() => clearErrors("address")}
              />
            </div>

            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <CustomInput
                  {...register("websiteUrl")}
                  id="website-url"
                  label={t(`${mode === "create" ? "createCommunity" : "editCommunity"}.website`)}
                  placeholder={
                    mode === "create"
                      ? t("createCommunity.websitePlaceholder")
                      : t("editCommunity.placeholders.website")
                  }
                  error={shouldShowError("description", "websiteUrl")}
                  onClearError={() => clearErrors("websiteUrl")}
                />
                <CustomInput
                  {...register("twitterUrl")}
                  id="twitter-url"
                  label={t(`${mode === "create" ? "createCommunity" : "editCommunity"}.twitter`)}
                  placeholder={
                    mode === "create"
                      ? t("createCommunity.twitterPlaceholder")
                      : t("editCommunity.placeholders.twitter")
                  }
                  error={shouldShowError("description", "twitterUrl")}
                  onClearError={() => clearErrors("twitterUrl")}
                />
                <CustomInput
                  {...register("instagramUrl")}
                  id="instagram-url"
                  label={t(`${mode === "create" ? "createCommunity" : "editCommunity"}.instagram`)}
                  placeholder={
                    mode === "create"
                      ? t("createCommunity.instagramPlaceholder")
                      : t("editCommunity.placeholders.instagram")
                  }
                  error={shouldShowError("description", "instagramUrl")}
                  onClearError={() => clearErrors("instagramUrl")}
                />
                <CustomInput
                  {...register("facebookUrl")}
                  id="facebook-url"
                  label={t(`${mode === "create" ? "createCommunity" : "editCommunity"}.facebook`)}
                  placeholder={
                    mode === "create"
                      ? t("createCommunity.facebookPlaceholder")
                      : t("editCommunity.placeholders.facebook")
                  }
                  error={shouldShowError("description", "facebookUrl")}
                  onClearError={() => clearErrors("facebookUrl")}
                />
              </div>
            </div>
          </div>
        ),
      },
      {
        id: "upload-media",
        title: t(`${tKey}.uploadMedia`),
        showNextButton: true,
        isComplete: sectionValidation["upload-media"]?.isComplete,
        hasError: !sectionValidation["upload-media"]?.isComplete,
        summary: sectionValidation["upload-media"]?.summary,
        onInfoClick: () => handleTipClick("upload-media"),
        content:
          mode === "edit" && editProps ? (
            <div className="space-y-4">
              <p className="text-[14px] ms-1 font-monteserat loading-[20px] text-body mb-5 mt-2">
                {t("editCommunity.uploadMediaDescription")}
              </p>
              <MediaUploadSection
                initialMedia={editInitialMedia}
                onMediaChange={editProps.onMediaChange}
                onCoverImageChange={editProps.onCoverImageChange}
                maxFiles={COMMUNITY_MEDIA_LIMITS.IMAGES.MAX_FILES}
                maxImageFileSize={COMMUNITY_MEDIA_LIMITS.IMAGES.MAX_FILE_SIZE}
                maxVideoFileSize={COMMUNITY_MEDIA_LIMITS.VIDEOS.MAX_FILE_SIZE}
                allowedImageTypes={[...COMMUNITY_MEDIA_LIMITS.IMAGES.ALLOWED_TYPES]}
                allowedVideoTypes={[...COMMUNITY_MEDIA_LIMITS.VIDEOS.ALLOWED_TYPES]}
                // Native (Cloudflare Stream) video, inline in the grid (parity
                // with rituals).
                nativeVideos={nativeVideos}
                isVideoFile={isVideoFile}
                onUploadVideo={uploadNativeVideo}
                onDeleteVideo={deleteNativeVideo}
                enableAiGeneration
                aiEntityType="community"
                aiEntityContext={{
                  name: nameValue,
                  description: descriptionValue,
                  type: communityType,
                }}
              />
              {mediaError && (
                <p className="text-xs text-brand-terracotta-bright mt-1">{mediaError}</p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-[14px] ms-1 font-monteserat loading-[20px] text-body mb-5 mt-2">
                {t("createCommunity.uploadMediaDescription")}
              </p>
              <MediaUploadSection
                onMediaChange={handleMediaChange}
                maxFiles={COMMUNITY_MEDIA_LIMITS.IMAGES.MAX_FILES}
                maxImageFileSize={COMMUNITY_MEDIA_LIMITS.IMAGES.MAX_FILE_SIZE}
                maxVideoFileSize={COMMUNITY_MEDIA_LIMITS.VIDEOS.MAX_FILE_SIZE}
                allowedImageTypes={[...COMMUNITY_MEDIA_LIMITS.IMAGES.ALLOWED_TYPES]}
                allowedVideoTypes={[...COMMUNITY_MEDIA_LIMITS.VIDEOS.ALLOWED_TYPES]}
                // Native video: queued during create, uploaded after the
                // community is created (flushPending), parity with rituals.
                nativeVideos={nativeVideos}
                isVideoFile={isVideoFile}
                onUploadVideo={uploadNativeVideo}
                onDeleteVideo={deleteNativeVideo}
                enableAiGeneration
                aiEntityType="community"
                aiEntityContext={{
                  name: nameValue,
                  description: descriptionValue,
                  type: typeValue,
                }}
              />
              {attemptedSections.has("upload-media") && mediaError && (
                <p className="text-xs text-brand-terracotta-bright mt-1">{mediaError}</p>
              )}
            </div>
          ),
      },
      {
        id: "users-roles",
        title: t(`${tKey}.usersRoles`),
        showNextButton: true,
        isComplete: sectionValidation["users-roles"]?.isComplete,
        hasError: !sectionValidation["users-roles"]?.isComplete,
        summary: sectionValidation["users-roles"]?.summary,
        userPreviews: sectionValidation["users-roles"]?.userPreviews,
        totalCount:
          mode === "edit" && editProps
            ? (() => {
                const existingIds = new Set(editProps.allMembers.map(m => m.id))
                const addedCount = selectedUsers.filter(
                  su => !existingIds.has(su.userId) && !editProps.removedUserIds.has(su.userId),
                ).length
                return editProps.allMembers.length - editProps.removedUserIds.size + addedCount
              })()
            : selectedUsers.length,
        onInfoClick: () => handleTipClick("users-roles"),
        content:
          mode === "edit" && editProps ? (
            <div className="space-y-6">
              <p className="text-body mt-2 px-5 md:px-0">
                {t("editCommunity.usersRolesDescription")}
              </p>

              {(editProps.allMembers.length > 0 || editProps.membersLoading) && (
                <div className="md:space-y-2 max-h-[400px] overflow-y-auto pr-2">
                  {editProps.membersLoading && (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                    </div>
                  )}

                  {(() => {
                    const existingMemberIds = new Set(editProps.allMembers.map(m => m.id))
                    const addedUsers = selectedUsers.filter(
                      su =>
                        !existingMemberIds.has(su.userId) &&
                        !editProps.removedUserIds.has(su.userId),
                    )

                    const allListedMembers = [
                      ...editProps.allMembers.filter(
                        member => !editProps.removedUserIds.has(member.id),
                      ),
                    ]

                    return (
                      <>
                        {allListedMembers.map(member => {
                          const selectedUser = selectedUsers.find(su => su.userId === member.id)
                          const memberRoleIds = selectedUser?.roleIds || member.roleIds || []

                          return (
                            <div
                              key={member.id}
                              className="flex items-center gap-3 border-t last:border-b border-[#2A2A2A] md:border-0 md:last:border-0 pl-5 py-2.5 pr-3.75 md:p-0"
                            >
                              <div className="w-12 h-12 rounded-full bg-themed flex-shrink-0 flex items-center justify-center overflow-hidden">
                                {member.coverImage ? (
                                  <img
                                    src={member.coverImage}
                                    alt={member.name || "User"}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <span className="font-semibold">
                                    {(member.name || member.username || "U")[0].toUpperCase()}
                                  </span>
                                )}
                              </div>

                              <div className="flex-1 min-w-0">
                                <p className="font-semibold truncate">
                                  {member.name || t("editCommunity.noName")}
                                </p>
                                <p className="text-sm truncate">
                                  {member.username ? `@${member.username}` : member.name}
                                </p>
                                <div className="md:hidden mt-1">
                                  {member.communityBuilder ? (
                                    <div className="md:min-w-[200px] font-bold rounded-lg text-[14px] text-primary text-left focus:outline-none flex items-center gap-3 opacity-50 cursor-not-allowed">
                                      <span className="truncate text-primary">Owner</span>
                                      <ChevronsUpDown size={16} strokeWidth={2.5} />
                                    </div>
                                  ) : (
                                    <MultiSelectDropdown
                                      options={roleOptions.map(o => o.label)}
                                      selectedOptions={
                                        memberRoleIds && memberRoleIds.length > 0
                                          ? roleOptions
                                              .filter(o => memberRoleIds.includes(o.value))
                                              .map(o => o.label)
                                          : ["Ritualist"]
                                      }
                                      onChange={labels => {
                                        const selectedRoleIds = roleOptions
                                          .filter(o => labels.includes(o.label))
                                          .map(o => o.value)
                                        onUpdateUserRoles(member.id, selectedRoleIds)
                                      }}
                                    />
                                  )}
                                </div>
                              </div>

                              <div className="hidden md:flex">
                                {member.communityBuilder ? (
                                  <div className="md:min-w-[200px] font-bold rounded-lg text-[14px] text-primary text-left focus:outline-none flex items-center justify-end gap-3 px-3 py-1.5 opacity-50 cursor-not-allowed">
                                    <span className="truncate">Owner</span>
                                    <ChevronsUpDown size={16} strokeWidth={2.5} />
                                  </div>
                                ) : (
                                  <MultiSelectDropdown
                                    options={roleOptions.map(o => o.label)}
                                    selectedOptions={
                                      memberRoleIds && memberRoleIds.length > 0
                                        ? roleOptions
                                            .filter(o => memberRoleIds.includes(o.value))
                                            .map(o => o.label)
                                        : ["Ritualist"]
                                    }
                                    onChange={labels => {
                                      const selectedRoleIds = roleOptions
                                        .filter(o => labels.includes(o.label))
                                        .map(o => o.value)
                                      onUpdateUserRoles(member.id, selectedRoleIds)
                                    }}
                                  />
                                )}
                              </div>

                              {!member.communityBuilder && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 flex-shrink-0"
                                    >
                                      <MoreVertical size={18} />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                      onClick={() => onRemoveUser(member.id)}
                                      className="text-red focus:text-red"
                                    >
                                      <Trash2 size={16} />
                                      {t("editCommunity.removeUser")}
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </div>
                          )
                        })}

                        {addedUsers.map(user => {
                          const memberRoleIds =
                            user.roleIds && user.roleIds.length > 0 ? user.roleIds : []

                          return (
                            <div
                              key={user.userId}
                              className="flex items-center gap-3 border-t last:border-b border-[#2A2A2A] md:border-0 md:last:border-0 pl-5 py-2.5 pr-3.75 md:p-0"
                            >
                              <div className="w-12 h-12 rounded-full bg-themed flex-shrink-0 flex items-center justify-center overflow-hidden">
                                {user.profilePictureUrl ? (
                                  <img
                                    src={user.profilePictureUrl}
                                    alt={user.name || "User"}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <span className="font-semibold">
                                    {(user.name || user.email || "U")[0].toUpperCase()}
                                  </span>
                                )}
                              </div>

                              <div className="flex-1 min-w-0">
                                <p className="font-semibold truncate">
                                  {user.name || t("editCommunity.noName")}
                                </p>
                                <p className="text-sm truncate">
                                  {user.username ? `@${user.username}` : user.email}
                                </p>
                                <div className="md:hidden mt-1">
                                  {user.isCurrentUser ? (
                                    <div className="md:min-w-[200px] font-bold rounded-lg text-[14px] text-primary text-left focus:outline-none flex items-center gap-3 opacity-50 cursor-not-allowed">
                                      <span className="truncate text-primary">Owner</span>
                                      <ChevronsUpDown size={16} strokeWidth={2.5} />
                                    </div>
                                  ) : (
                                    <MultiSelectDropdown
                                      options={roleOptions.map(o => o.label)}
                                      selectedOptions={
                                        memberRoleIds.length > 0
                                          ? roleOptions
                                              .filter(o => memberRoleIds.includes(o.value))
                                              .map(o => o.label)
                                          : ["Ritualist"]
                                      }
                                      onChange={labels => {
                                        const selectedRoleIds = roleOptions
                                          .filter(o => labels.includes(o.label))
                                          .map(o => o.value)
                                        onUpdateUserRoles(user.userId, selectedRoleIds)
                                      }}
                                    />
                                  )}
                                </div>
                              </div>

                              <div className="hidden md:flex">
                                {user.isCurrentUser ? (
                                  <div className="md:min-w-[200px] font-bold rounded-lg text-[14px] text-primary text-left focus:outline-none flex items-center justify-end gap-3 px-3 py-1.5 opacity-50 cursor-not-allowed">
                                    <span className="truncate">Owner</span>
                                    <ChevronsUpDown size={16} strokeWidth={2.5} />
                                  </div>
                                ) : (
                                  <MultiSelectDropdown
                                    options={roleOptions.map(o => o.label)}
                                    selectedOptions={
                                      memberRoleIds.length > 0
                                        ? roleOptions
                                            .filter(o => memberRoleIds.includes(o.value))
                                            .map(o => o.label)
                                        : ["Ritualist"]
                                    }
                                    onChange={labels => {
                                      const selectedRoleIds = roleOptions
                                        .filter(o => labels.includes(o.label))
                                        .map(o => o.value)
                                      onUpdateUserRoles(user.userId, selectedRoleIds)
                                    }}
                                  />
                                )}
                              </div>

                              {!user.isCurrentUser && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 flex-shrink-0"
                                    >
                                      <MoreVertical size={18} />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                      onClick={() => onRemoveUser(user.userId)}
                                      className="text-red focus:text-red"
                                    >
                                      <Trash2 size={16} />
                                      {t("editCommunity.removeUser")}
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </div>
                          )
                        })}
                      </>
                    )
                  })()}

                  {editProps.hasNextPage && (
                    <div
                      ref={editProps.loadMoreRef}
                      className="flex items-center justify-center py-4"
                    >
                      {editProps.isFetchingNextPage ? (
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                      ) : (
                        <span className="text-sm text-gray-500">Scroll for more...</span>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-4 px-5 md:px-0">
                <Button
                  variant="ghost"
                  onClick={() => setIsUserModalOpen(true)}
                  className="flex items-center gap-2 px-3 h-8 rounded-lg text-sm font-medium text-primary"
                >
                  <Plus size={18} />
                  {t("editCommunity.addMore")}
                </Button>
              </div>

              <SelectCommunityUsersModal
                isOpen={isUserModalOpen}
                onClose={() => setIsUserModalOpen(false)}
                onAddUsers={onAddUsers}
                existingUserIds={[
                  ...new Set([
                    ...selectedUsers.map(u => u.userId),
                    ...editProps.allMembers.map(m => m.id),
                  ]),
                ]}
                communityId={communityId}
                communityRoles={communityRoles}
                currentUser={
                  currentUser
                    ? {
                        id: currentUser.id,
                        name: currentUser.name,
                        email: currentUser.email,
                        username: currentUser.username,
                        profilePictureUrl:
                          currentUser.profile_photos?.find(p => p.isCover || p.is_cover_image)
                            ?.filePath || currentUser.profilePictureUrl,
                        profilePhotos: currentUser.profile_photos,
                      }
                    : undefined
                }
                ownerRoleId={ownerRoleId}
              />
            </div>
          ) : (
            <div className="space-y-6">
              <p className="text-body mt-2 px-5 md:px-0">
                {t("createCommunity.moderatorsRolesDescription")}
              </p>

              {selectedUsers.length > 0 && (
                <div className="md:space-y-6">
                  {selectedUsers.map(user => (
                    <div
                      key={user.userId}
                      className="flex items-center gap-3 border-t last:border-b border-[#2A2A2A] md:border-0 md:last:border-0 pl-5 py-2.5 pr-3.75 md:p-0"
                    >
                      <div className="w-12 h-12 rounded-full bg-themed flex-shrink-0 flex items-center justify-center overflow-hidden">
                        {user.profilePictureUrl ? (
                          <img
                            src={user.profilePictureUrl}
                            alt={user.name || user.email}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="font-semibold">
                            {(user.name || user.email)[0].toUpperCase()}
                          </span>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">
                          {user.name || t("createCommunity.noName")}
                        </p>
                        <p className="text-sm truncate">
                          {user.username ? `@${user.username}` : user.email}
                        </p>
                        <div className="md:hidden mt-1">
                          {user.isCurrentUser ? (
                            <div className="md:min-w-[200px] font-bold rounded-lg text-[14px] text-primary text-left focus:outline-none flex items-center gap-3 opacity-50 cursor-not-allowed">
                              <span className="truncate text-primary">Owner</span>
                              <ChevronsUpDown size={16} strokeWidth={2.5} />
                            </div>
                          ) : (
                            <MultiSelectDropdown
                              options={roleOptions.map(o => o.label)}
                              selectedOptions={
                                user.roleIds && user.roleIds.length > 0
                                  ? roleOptions
                                      .filter(o => user.roleIds.includes(o.value))
                                      .map(o => o.label)
                                  : ["Ritualist"]
                              }
                              onChange={labels => {
                                const selectedRoleIds = roleOptions
                                  .filter(o => labels.includes(o.label))
                                  .map(o => o.value)
                                onUpdateUserRoles(user.userId, selectedRoleIds)
                              }}
                            />
                          )}
                        </div>
                      </div>

                      <div className="hidden md:flex">
                        {user.isCurrentUser ? (
                          <div className=" md:min-w-[200px] font-bold rounded-lg text-[14px] text-primary text-left focus:outline-none flex items-center justify-end gap-3 px-3 py-1.5 opacity-50 cursor-not-allowed">
                            <span className="truncate text-primary">Owner</span>
                            <ChevronsUpDown size={16} strokeWidth={2.5} />
                          </div>
                        ) : (
                          <MultiSelectDropdown
                            options={roleOptions.map(o => o.label)}
                            selectedOptions={
                              user.roleIds && user.roleIds.length > 0
                                ? roleOptions
                                    .filter(o => user.roleIds.includes(o.value))
                                    .map(o => o.label)
                                : ["Ritualist"]
                            }
                            onChange={labels => {
                              const selectedRoleIds = roleOptions
                                .filter(o => labels.includes(o.label))
                                .map(o => o.value)
                              onUpdateUserRoles(user.userId, selectedRoleIds)
                            }}
                          />
                        )}
                      </div>

                      {!user.isCurrentUser && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                              <MoreVertical size={18} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => onRemoveUser(user.userId)}
                              className="text-red focus:text-red"
                            >
                              <Trash2 size={16} />
                              {t("createCommunity.removeUser")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-4 px-5 md:px-0">
                <Button
                  variant="ghost"
                  onClick={() => setIsUserModalOpen(true)}
                  className="flex items-center gap-2 px-3 h-8 rounded-lg text-sm font-medium text-primary"
                >
                  <Plus size={18} />
                  {t("createCommunity.addMembers")}
                </Button>
              </div>

              {attemptedSections.has("users-roles") && errors.selectedUsers && (
                <p className="text-xs text-brand-terracotta-bright mt-1 px-5 md:px-0">
                  {errors.selectedUsers.message}
                </p>
              )}

              <SelectCommunityUsersModal
                isOpen={isUserModalOpen}
                onClose={() => setIsUserModalOpen(false)}
                onAddUsers={onAddUsers}
                existingUserIds={selectedUsers.map(u => u.userId)}
                communityRoles={communityRoles}
                currentUser={
                  currentUser
                    ? {
                        id: currentUser.id,
                        name: currentUser.name,
                        email: currentUser.email,
                        username: currentUser.username,
                        profilePictureUrl:
                          currentUser.profile_photos?.find(p => p.isCover || p.is_cover_image)
                            ?.filePath || currentUser.profilePictureUrl,
                        profilePhotos: currentUser.profile_photos,
                      }
                    : undefined
                }
                ownerRoleId={ownerRoleId}
              />
            </div>
          ),
      },
      {
        id: "guidelines",
        title: t(`${tKey}.guidelines`),
        showNextButton: true,
        isComplete: sectionValidation.guidelines?.isComplete,
        hasError:
          mode === "edit"
            ? !sectionValidation.guidelines?.isComplete
            : !sectionValidation.guidelines?.isComplete && attemptedSections.has("guidelines"),
        summary: sectionValidation.guidelines?.summary,
        onInfoClick: () => handleTipClick("guidelines"),
        content: (
          <div className="space-y-4 mb-5">
            <p className="text-sm mb-7 text-body mt-2">
              {mode === "create"
                ? t("createCommunity.guidelinesDescription")
                : t("editCommunity.guidelinesDescription")}
            </p>
            <CustomTextarea
              {...register("guidelines")}
              id="community-guidelines"
              label={t(`${tKey}.guidelines`)}
              required
              placeholder={
                mode === "create"
                  ? t("createCommunity.guidelinesPlaceholder")
                  : t("editCommunity.placeholders.guidelines")
              }
              error={shouldShowError("guidelines", "guidelines")}
              onClearError={() => clearErrors("guidelines")}
              rows={4}
            />
          </div>
        ),
      },
      {
        id: "privacy",
        title:
          mode === "create" ? t("createCommunity.privacy") : t("editCommunity.privacySettings"),
        showNextButton: true,
        isComplete: sectionValidation.privacy?.isComplete,
        hasError: !sectionValidation.privacy?.isComplete,
        summary: sectionValidation.privacy?.summary,
        onInfoClick: () => handleTipClick("privacy"),
        content: (
          <div className="space-y-3">
            <p className="text-body mt-[6px] text-[14px] loading-[20px]">
              {t("createCommunity.privacyDescription")}
            </p>

            <div className="flex flex-col md:flex-row text=[12px] gap-1.25 md:items-center md:justify-between">
              <label className="font-semibold text-primary block">
                {t("createCommunity.type")}
              </label>
              <Select
                value={communityType}
                onValueChange={v => onCommunityTypeChange(v as "public" | "private")}
              >
                <SelectTrigger className="font-semibold text-[14px] px-3.75 py-2.5 rounded-full md:rounded-none md:p-0 bg-[#F2F0EB1A] md:bg-transparent">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">Public - Everyone can join</SelectItem>
                  <SelectItem value="private">Private - Invite only / Request to join</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3.75 md:space-y-6">
              <MultiSelectDropdown
                label={
                  mode === "create"
                    ? t("createCommunity.whoCanAddToRituals")
                    : t("editCommunity.whoCanAddToRituals")
                }
                options={["Owners", "Moderators", "Ritualists", "Members"]}
                selectedOptions={privacySettings.addToRituals}
                onChange={onAddToRitualsChange}
                disabledOptions={["Owners"]}
              />

              <MultiSelectDropdown
                label={
                  mode === "create"
                    ? t("createCommunity.whoCanComment")
                    : t("editCommunity.whoCanComment")
                }
                options={["Owners", "Moderators", "Ritualists", "Members"]}
                selectedOptions={privacySettings.comment}
                onChange={onCommentChange}
                disabledOptions={["Owners"]}
              />
            </div>
          </div>
        ),
      },
    ]

    return items
  }, [
    mode,
    t,
    register,
    errors,
    clearErrors,
    setValue,
    watch,
    sectionValidation,
    attemptedSections,
    mediaError,
    tags,
    onAddTag,
    onRemoveTag,
    selectedUsers,
    onAddUsers,
    onRemoveUser,
    onUpdateUserRoles,
    communityType,
    onCommunityTypeChange,
    privacySettings,
    onAddToRitualsChange,
    onCommentChange,
    selectedLocation,
    onLocationSelect,
    roleOptions,
    currentUser,
    communityRoles,
    ownerRoleId,
    communityId,
    nameValue,
    descriptionValue,
    typeValue,
    editProps,
    isUserModalOpen,
    shouldShowError,
    handleTipClick,
    handleMediaChange,
    editInitialMedia,
    deleteNativeVideo,
    nativeVideos,
    isVideoFile,
    uploadNativeVideo,
  ])

  const accordionItems = getAccordionItems()

  const handleNext = useCallback(
    async (currentIndex: number): Promise<boolean> => {
      const currentAccordionId = accordionItems[currentIndex].id

      let fieldsToValidate: (keyof CreateCommunityFormData)[] = []

      switch (currentAccordionId) {
        case "community-info":
          fieldsToValidate = ["name", "handle"]
          break
        case "description":
          fieldsToValidate = ["description", "tags", "address"]
          break
        case "upload-media": {
          if (mode === "edit" && editProps) {
            const items = editProps.mediaChanges?.mediaItems ?? []
            const images = items.filter(i => i.mediaType === "IMAGE")
            if (images.length === 0) {
              setMediaError(t("editCommunity.imageRequired"))
              return false
            }
            if (!editProps.hasCoverImage) {
              setMediaError(t("editCommunity.selectCoverImage"))
              return false
            }
            setMediaError(null)
            return true
          }
          if ((createMediaFiles?.images?.length ?? 0) === 0) {
            setMediaError(t("createCommunity.imageRequired"))
            return false
          }
          const hasCover = createMediaFiles?.images?.some(img => img.isCover) ?? false
          if (!hasCover) {
            setMediaError(t("createCommunity.selectCoverImage"))
            return false
          }
          setMediaError(null)
          return true
        }
        case "users-roles": {
          if (mode === "edit" && selectedUsers.length === 0) {
            toast.error(t("editCommunity.memberRequired"))
            return false
          }
          fieldsToValidate = ["selectedUsers"]
          break
        }
        case "guidelines":
          fieldsToValidate = ["guidelines"]
          break
        default:
          return true
      }

      const isValid = await trigger(fieldsToValidate)
      return isValid
    },
    [accordionItems, mode, editProps, createMediaFiles, selectedUsers, trigger, t],
  )

  const isFormDisabled = !isValid

  const sectionTips = getCommunitySectionTips(openAccordionId)

  return (
    <div className="min-h-screen max-w-360 mx-auto px-5 md:px-17.5">
      <div className="max-w-8xl mx-auto mb-4 md:py-10">
        {mode === "edit" && (
          <div className="hidden md:block mb-6">
            <BackButton className="bg-card border-card" label={t("communityHeader.back")} />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 max-w-[850px]" ref={accordionContainerRef}>
            <Accordion
              ref={accordionRef}
              items={accordionItems}
              defaultOpenIndex={0}
              allowMultiple={false}
              onNextClick={handleNext}
              onOpenChange={handleAccordionOpenChange}
              onSectionAttempt={sectionId => {
                setAttemptedSections(prev => new Set([...prev, sectionId]))
              }}
              accordionItemRef={setAccordionRef}
              attemptedSections={attemptedSections}
            />
          </div>

          <div className="sticky md:top-4">
            <div
              className="hidden lg:block"
              style={{
                height: `${sidebarOffset}px`,
                transition: "height 250ms ease-in-out",
              }}
            />

            <div
              className={cn(
                "rounded-[20px] border border-dashed p-5 transition-colors bg-card border-card hidden lg:block",
              )}
              style={{
                transition: "background-color 250ms ease-in-out, border-color 250ms ease-in-out",
              }}
            >
              <div>
                <h3 className="text-[18px] leading-6.25 font-semibold text-primary mb-3.75">
                  {sectionTips.title}
                </h3>
              </div>
              <ul className="font-montserrat text-[14px] font-normal leading-[25px] text-primary list-none">
                {sectionTips.tips.map((tip, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span>.</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:mt-6 border bg-card border-card rounded-lg p-5">
              {submitError && (
                <div className="p-3 rounded-md">
                  <p className="text-sm text-brand-terracotta-bright">{submitError}</p>
                </div>
              )}

              <Button
                onClick={() => onSubmit()}
                disabled={isSubmitting || isFormDisabled}
                className="w-full uppercase text-sm"
                variant="secondary"
              >
                {isSubmitting
                  ? mode === "create"
                    ? t("createCommunity.creating")
                    : t("editCommunity.updating")
                  : mode === "create"
                    ? t("createCommunity.createButton")
                    : t("editCommunity.updateButton")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={tipModalOpen} onOpenChange={setTipModalOpen}>
        <DialogContent className="sm:max-w-[425px] !fixed !top-[5%] !translate-y-0 rounded-[20px] border border-dashed p-5 transition-colors bg-card border-card">
          <DialogHeader>
            <DialogTitle className="text-[18px] leading-6.25 font-semibold text-primary text-start">
              {sectionTips.title}
            </DialogTitle>
          </DialogHeader>
          <ul className="font-montserrat text-[14px] font-normal leading-[25px] text-primary list-none space-y-1">
            {tipModalSectionId &&
              getCommunitySectionTips(tipModalSectionId).tips.map((tip, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span>.</span>
                  <span>{tip}</span>
                </li>
              ))}
          </ul>
        </DialogContent>
      </Dialog>

      {renderModals?.()}
    </div>
  )
}
